import React, { useState, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
// import LangSwitcher from "../LangSwitcher";
import {
  peekPrevSafeRoute,
  popToPrevSafeRoute,
} from "../../hooks/useLastSafeRoute";

function NavBar({
  className = "",
  menuOpen = false,
  onToggleMenu,
  onCloseMenu,
  guidePath,
  handleSignOut,
}) {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  // Bottom-nav pages (logo shown on these; otherwise show back button)
  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();
  const ifuHref = `https://www.electronrx.com/instructions-for-use-addenbrookes`;

  const path = location?.pathname || "";

  // Logo should show on bottom-nav pages EXCEPT home
  const showLogo = path === "/notice" || path === "/about";

  // Home should show neither logo nor back button
  const hideLeftControl = path === "/home";

  // back is only available if we actually have a safe previous route
  const hasSafeBack = useMemo(() => {
    return !hideLeftControl && !showLogo;
  }, [hideLeftControl, showLogo]);

  const showBackButton = !showLogo && !hideLeftControl && hasSafeBack;

  const handleNavigate = useCallback(() => {
    onCloseMenu?.();
  }, [onCloseMenu]);

  // inside NavBar.js
  const handleBack = useCallback(() => {
    onCloseMenu?.();

    const current = location?.pathname || "";

    const dest = popToPrevSafeRoute("/home", current);

    navigate(dest); // do NOT replace while debugging
  }, [navigate, onCloseMenu, location?.pathname]);

  return (
    <>
      <header
        className={`grid grid-cols-3 items-center w-full gap-4 z-50 mb-2 ${!menuOpen && "md:border-b md:border-greyscale-3"} ${className}`}
      >
        {/* Left: logo OR back button */}
        <div className="flex items-center gap-2 min-w-0 justify-start py-1 pl-2 md:pl-4">
          {showBackButton ? (
            <button
              type="button"
              onClick={handleBack}
              className="group flex !flex-row gap-1"
              aria-label={t("navBar.back", "Back")}
            >
              <span
                className="material-symbols-outlined text-typogrpahy text-sm group-hover:text-primary group-focus-visible:text-primary"
                aria-hidden="true"
              >
                arrow_back_ios
              </span>
            </button>
          ) : showLogo ? (
            <Link
              className="flex-shrink-0 w-fit"
              to="/home"
              aria-label="Home"
              onClick={handleNavigate}
            >
              <img
                src="/purpledx_primary_logo.png"
                alt="purpleDx"
                className="h-10 w-auto max-w-none title-text"
                loading="lazy"
              />
            </Link>
          ) : (
            <div className="h-12 w-[120px]" aria-hidden="true" />
          )}
        </div>

        {/* CENTER (desktop nav perfectly centered in viewport) */}
        <div className="flex justify-center w-full">
          <nav className="hidden md:flex items-center gap-4 self-center w-full max-w-lg justify-between ">
            {/* Home */}
            <Link to="/home" className="nav-title" onClick={onCloseMenu}>
              {t("navBar.home", "Home")}
            </Link>

            {/* IFU */}
            <a
              href={ifuHref}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-title"
              onClick={onCloseMenu}
            >
              {t("navBar.ifu_short", "Instructions")}
            </a>

            {/* Results */}
            <Link to="/results" className="nav-title" onClick={onCloseMenu}>
              {t("navBar.results", "Results")}
            </Link>

            {/* Menu toggle */}
            <button
              type="button"
              className={["nav-title", menuOpen ? "text-primary" : ""].join(
                " ",
              )}
              onClick={onToggleMenu}
              aria-expanded={menuOpen}
              aria-label={t("navBar.menu", "Menu")}
            >
              {t("navBar.menu", "Menu")}
            </button>
          </nav>{" "}
        </div>

        {/* Contextual Guide link (only visible on /recording/<variant>/camera) */}
        <div className="self-end hidden md:flex p-1 pr-2">
          {guidePath && (
            <Link
              to={guidePath}
              className="small-outlined-button group flex !flex-row gap-1"
            >
              <span
                className="material-symbols-outlined text-primary text-xs
                  group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
              >
                info
              </span>{" "}
              {t("navBar.guide", "Guide")}
            </Link>
          )}
        </div>

        {/* Mobile right: Guide CTA + hamburger */}
        <div className="md:hidden flex flex-row gap-4 w-full items-center justify-end p-1 pr-2">
          {guidePath && (
            <Link
              to={guidePath}
              className="small-outlined-button md:hidden group flex !flex-row gap-1"
            >
              <span
                className="material-symbols-outlined text-primary text-xs
                  group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
              >
                info
              </span>
              {t("navBar.guide", "Guide")}
            </Link>
          )}
        </div>
      </header>
    </>
  );
}

export default React.memo(NavBar);
